import { registerSharedDependency } from 'core-components/helpers/MFHelper'

interface StoreEntry<T = unknown> {
  value: T
  inflight: Promise<T> | null
  expiresAt: number
}

/**
 * Lifetime of a resolved value.
 *
 * Long enough to absorb the burst of identical requests fired while a page's
 * widgets mount (request-detail and action-list ask for the same request and
 * actions), short enough that nothing can be served stale indefinitely.
 * Entries used to live forever, which made every React Query refetch a no-op:
 * after executing a transition the refetch returned the pre-transition actions
 * and only a full page reload showed the new state.
 */
const TTL_MS = 5_000

/**
 * Shared reactive data store for onboarding widgets.
 *
 * - Singleton via registerSharedDependency (survives code-splitting)
 * - Deduplicates in-flight fetches
 * - Caches resolved data for a short TTL so widgets mounting together share one
 *   HTTP request; it is NOT a replacement for the React Query cache
 * - Notifies subscribers via useSyncExternalStore-compatible API
 *
 * Usage in repositories:
 *   return onboardingDataStore.fetch('actions:123', () => api.getActions(123))
 *
 * Usage in React (via useOnboardingData hook):
 *   const data = useOnboardingData('actions:123', () => repo.getActions(123))
 *
 * Anything that mutates onboarding data must invalidate this store as well as
 * the React Query cache — see `invalidateOnboardingQueries`.
 */
class OnboardingDataStore {
  private entries = new Map<string, StoreEntry>()
  private subscribers: (() => void)[] = []
  private version = 0

  get<T>(key: string): T | undefined {
    const entry = this.entries.get(key)
    if (!entry || entry.expiresAt <= Date.now()) {
      return undefined
    }
    return entry.value as T | undefined
  }

  set<T>(key: string, value: T): void {
    const expiresAt = Date.now() + TTL_MS
    const entry = this.entries.get(key)
    if (entry) {
      entry.value = value
      entry.inflight = null
      entry.expiresAt = expiresAt
    } else {
      this.entries.set(key, { value, inflight: null, expiresAt })
    }
    this.notify()
  }

  /**
   * Fetches data with deduplication and short-lived caching.
   * - If a fetch is in flight, returns that promise
   * - If a fresh value is cached, returns it immediately
   * - Otherwise calls fetcher, caches the result, notifies subscribers
   */
  async fetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const existing = this.entries.get(key)

    // Return in-flight promise (deduplication)
    if (existing?.inflight) {
      return existing.inflight as Promise<T>
    }

    // Return cached data while it is fresh
    if (existing && existing.value !== undefined && existing.expiresAt > Date.now()) {
      return existing.value as T
    }

    // New fetch
    const promise: Promise<T> = fetcher()
      .then((value) => {
        // An invalidation may have landed while this was in flight (a reopen
        // invalidates twice in a row). Caching the value now would put
        // pre-mutation data back with a fresh lifetime, which is the exact
        // staleness this store's TTL exists to prevent — so only the fetch that
        // is still the current one for this key may write.
        if (this.entries.get(key)?.inflight === promise) {
          this.set(key, value)
        }
        return value
      })
      .catch((err) => {
        // Clear inflight on error so retry is possible — but only our own, a
        // later fetch for the same key owns the entry now.
        const entry = this.entries.get(key)
        if (entry?.inflight === promise) {
          entry.inflight = null
        }
        throw err
      })

    if (existing) {
      existing.inflight = promise
    } else {
      this.entries.set(key, { value: undefined as T, inflight: promise, expiresAt: 0 })
    }

    return promise
  }

  invalidate(key: string): void {
    this.entries.delete(key)
    this.notify()
  }

  invalidateByPrefix(prefix: string): void {
    for (const key of this.entries.keys()) {
      if (key.startsWith(prefix)) {
        this.entries.delete(key)
      }
    }
    this.notify()
  }

  invalidateAll(): void {
    this.entries.clear()
    this.notify()
  }

  // useSyncExternalStore-compatible API
  subscribe = (cb: () => void): (() => void) => {
    this.subscribers.push(cb)
    return () => {
      this.subscribers = this.subscribers.filter((fn) => fn !== cb)
    }
  }

  getVersion = (): number => {
    return this.version
  }

  private notify(): void {
    this.version++
    this.subscribers.forEach((cb) => cb())
  }
}

export const onboardingDataStore = registerSharedDependency<OnboardingDataStore>(
  'onboarding__data-store',
  () => new OnboardingDataStore()
)
